import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Card, Spin, Alert as AntAlert, Button, Tag, Tooltip } from 'antd';
import { ReloadOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTheme } from '../../contexts/useTheme';
import type { EventAlert, AlertType, AlertSeverity } from '../../api/alertsService';
import type { Session } from '../../api/eventsService';
import { ALERT_TYPE_CONFIG, SEVERITY_CONFIG } from '../../api/alertsService';
import { getEventDateStatus } from '../../utils/eventSource';
import './AlertsTimeline.css';

dayjs.extend(utc);
dayjs.extend(timezone);

interface AlertsTimelineProps {
  alerts: EventAlert[];
  sessions?: Session[];
  eventStartDate: string;  // YYYY-MM-DD
  eventEndDate: string;    // YYYY-MM-DD
  circuitTimeZone?: string;
  loading?: boolean;
  refreshing?: boolean; // For refresh button spinner during silent updates
  // Header props
  onRefresh?: () => void;
  onCreateAlert?: () => void;
  canManageAlerts?: boolean;
  hasAdminRole?: boolean;
  error?: string | null;
  onClearError?: () => void;
}

// Alert type definitions for swim lanes
const ALERT_TYPE_LANES: Array<{ type: AlertType; label: string; color: string; icon: string }> = [
  { type: 'air_temperature', label: 'Temp', color: ALERT_TYPE_CONFIG.air_temperature.color, icon: ALERT_TYPE_CONFIG.air_temperature.icon },
  { type: 'rain_risk', label: 'Rain', color: ALERT_TYPE_CONFIG.rain_risk.color, icon: ALERT_TYPE_CONFIG.rain_risk.icon },
  { type: 'weather_alert', label: 'Weather', color: ALERT_TYPE_CONFIG.weather_alert.color, icon: ALERT_TYPE_CONFIG.weather_alert.icon },
  { type: 'track_condition', label: 'Track', color: ALERT_TYPE_CONFIG.track_condition.color, icon: ALERT_TYPE_CONFIG.track_condition.icon },
  { type: 'system_status', label: 'System', color: ALERT_TYPE_CONFIG.system_status.color, icon: ALERT_TYPE_CONFIG.system_status.icon },
  { type: 'general', label: 'General', color: ALERT_TYPE_CONFIG.general.color, icon: ALERT_TYPE_CONFIG.general.icon },
];

// Get severity color
const getSeverityColor = (severity: AlertSeverity): string => {
  return SEVERITY_CONFIG[severity]?.color || '#8c8c8c';
};

// Get severity color with alpha
function getSeverityColorWithAlpha(severity: AlertSeverity, alpha: number = 1): string {
  const color = getSeverityColor(severity);
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Group alerts by their type for swim lane display
function groupAlertsByType(alerts: EventAlert[]): Map<AlertType, EventAlert[]> {
  const groups = new Map<AlertType, EventAlert[]>();
  
  // Initialize all type groups
  ALERT_TYPE_LANES.forEach(lane => {
    groups.set(lane.type, []);
  });
  
  for (const alert of alerts) {
    if (alert.is_deleted) continue;
    const group = groups.get(alert.alert_type);
    if (group) {
      group.push(alert);
    }
  }
  
  return groups;
}

export const AlertsTimeline: React.FC<AlertsTimelineProps> = ({
  alerts,
  sessions = [],
  eventStartDate,
  eventEndDate,
  circuitTimeZone,
  loading = false,
  refreshing = false,
  onRefresh,
  onCreateAlert,
  canManageAlerts = false,
  hasAdminRole = false,
  error,
  onClearError,
}) => {
  const { theme } = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const eventDateStatus = useMemo(
    () => getEventDateStatus(eventStartDate, eventEndDate, circuitTimeZone),
    [eventStartDate, eventEndDate, circuitTimeZone]
  );
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [hoveredAlert, setHoveredAlert] = useState<EventAlert | null>(null);
  const [hoveredSession, setHoveredSession] = useState<Session | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [canvasRenderKey, setCanvasRenderKey] = useState(0);
  
  // Group alerts by type for swim lanes
  const alertsByType = useMemo(() => groupAlertsByType(alerts), [alerts]);
  
  // Update current time every minute
  useEffect(() => {
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();
    
    const alignTimeout = setTimeout(() => {
      setCurrentTime(new Date());
      const interval = setInterval(() => {
        setCurrentTime(new Date());
      }, 60 * 1000);
      
      return () => clearInterval(interval);
    }, msUntilNextMinute);
    
    return () => clearTimeout(alignTimeout);
  }, []);
  
  // Re-draw canvas when container size changes (e.g. window resize)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      setCanvasRenderKey((prev) => prev + 1);
    });
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [loading, eventStartDate, eventEndDate]);
  
  // Get event time bounds
  const getEventBounds = useCallback(() => {
    const tz = circuitTimeZone || 'UTC';
    const startDate = dayjs.tz(eventStartDate, tz).startOf('day').toDate();
    const endDate = dayjs.tz(eventEndDate, tz).endOf('day').toDate();
    return { start: startDate, end: endDate };
  }, [eventStartDate, eventEndDate, circuitTimeZone]);
  
  // Convert date to pixel position
  const dateToX = useCallback((date: Date, bounds: { start: Date; end: Date }, width: number): number => {
    const totalDuration = bounds.end.getTime() - bounds.start.getTime();
    const elapsed = date.getTime() - bounds.start.getTime();
    return (elapsed / totalDuration) * width;
  }, []);
  
  // Layout constants
  const LABEL_WIDTH = 65; // Width for type labels on the left
  const LANE_HEIGHT = 18; // Height of each swim lane
  const LANE_PADDING = 2; // Padding between lanes
  const SESSION_LABEL_HEIGHT = 14; // Height for session labels at top
  const DATE_LABEL_HEIGHT = 22; // Height for date labels at bottom
  
  // Calculate canvas height based on swim lanes
  const canvasHeight = SESSION_LABEL_HEIGHT + (LANE_HEIGHT + LANE_PADDING) * ALERT_TYPE_LANES.length + DATE_LABEL_HEIGHT;
  
  // Format the current time for display
  const currentTimeDisplay = useMemo(() => {
    const tz = circuitTimeZone || 'UTC';
    return dayjs(currentTime).tz(tz).format('MMM D, YYYY, HH:mm');
  }, [currentTime, circuitTimeZone]);
  
  // Draw timeline
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const bounds = getEventBounds();
    const { width, height } = canvas.getBoundingClientRect();
    
    // Set canvas resolution for retina displays
    canvas.width = width * window.devicePixelRatio;
    canvas.height = height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    
    const isDark = theme === 'dark';
    const timelineWidth = width - LABEL_WIDTH;
    const timelineLeft = LABEL_WIDTH;
    
    // Helper to convert date to X position within the timeline area
    const dateToTimelineX = (date: Date) => {
      return timelineLeft + dateToX(date, bounds, timelineWidth);
    };
    
    // Clear canvas with background color
    ctx.fillStyle = isDark ? '#141414' : '#ffffff';
    ctx.fillRect(0, 0, width, height);
    
    // Draw past time overlay
    const nowX = dateToTimelineX(currentTime);
    if (currentTime > bounds.start) {
      const pastEndX = Math.min(nowX, width);
      ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)';
      ctx.fillRect(timelineLeft, 0, pastEndX - timelineLeft, height - DATE_LABEL_HEIGHT);
    }
    
    const totalDays = (bounds.end.getTime() - bounds.start.getTime()) / (1000 * 60 * 60 * 24);
    const [startYear, startMonth, startDay] = eventStartDate.split('-').map(Number);
    
    // Draw quarter-day lines
    ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)';
    ctx.lineWidth = 1;
    
    for (let day = 0; day <= Math.ceil(totalDays); day++) {
      for (const hour of [6, 12, 18]) {
        const date = new Date(bounds.start.getTime() + (day * 24 + hour) * 60 * 60 * 1000);
        const x = dateToTimelineX(date);
        
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height - DATE_LABEL_HEIGHT);
        ctx.stroke();
      }
    }
    
    // Draw day boundary lines
    ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)';
    ctx.lineWidth = 1;
    ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)';
    ctx.font = '9px sans-serif';
    ctx.textAlign = 'center';
    
    for (let day = 0; day <= Math.ceil(totalDays); day++) {
      const date = new Date(bounds.start.getTime() + day * 24 * 60 * 60 * 1000);
      const x = dateToTimelineX(date);
      
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
      
      const labelDate = new Date(startYear, startMonth - 1, startDay + day);
      const dayOfWeek = labelDate.toLocaleDateString('en-US', { weekday: 'short' });
      const dateLabel = labelDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      ctx.fillText(dayOfWeek, x, height - 12);
      ctx.fillText(dateLabel, x, height - 2);
    }
    
    // Draw sessions as background shading
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    
    sessions.forEach(session => {
      const sessionStart = new Date(session.start_time);
      const sessionEnd = new Date(session.end_time);
      const startX = dateToTimelineX(sessionStart);
      const endX = dateToTimelineX(sessionEnd);
      
      // Session background
      ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)';
      ctx.fillRect(startX, SESSION_LABEL_HEIGHT, endX - startX, height - SESSION_LABEL_HEIGHT - DATE_LABEL_HEIGHT);
      
      // Session label at top
      ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)';
      ctx.fillText(session.session_name, (startX + endX) / 2, 10);
    });
    
    // Draw swim lane labels and backgrounds
    ctx.textAlign = 'right';
    ctx.font = '9px sans-serif';
    
    ALERT_TYPE_LANES.forEach((lane, index) => {
      const laneY = SESSION_LABEL_HEIGHT + index * (LANE_HEIGHT + LANE_PADDING);
      
      // Lane background (alternating subtle shading)
      if (index % 2 === 0) {
        ctx.fillStyle = isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)';
        ctx.fillRect(timelineLeft, laneY, timelineWidth, LANE_HEIGHT);
      }
      
      // Lane label
      ctx.fillStyle = lane.color;
      ctx.fillText(lane.label, LABEL_WIDTH - 6, laneY + LANE_HEIGHT - 4);
      
      // Lane icon
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(lane.icon, 4, laneY + LANE_HEIGHT - 4);
      ctx.font = '9px sans-serif';
      ctx.textAlign = 'right';
    });
    
    // Draw vertical separator line between labels and timeline
    ctx.strokeStyle = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(timelineLeft, 0);
    ctx.lineTo(timelineLeft, height - DATE_LABEL_HEIGHT);
    ctx.stroke();
    
    // Draw alerts as bars in their respective lanes
    ALERT_TYPE_LANES.forEach((lane, laneIndex) => {
      const laneY = SESSION_LABEL_HEIGHT + laneIndex * (LANE_HEIGHT + LANE_PADDING);
      const laneAlerts = alertsByType.get(lane.type) || [];
      
      laneAlerts.forEach(alert => {
        const alertStart = new Date(alert.start_time);
        const alertEnd = new Date(alert.end_time);
        const startX = dateToTimelineX(alertStart);
        const endX = dateToTimelineX(alertEnd);
        
        const y = laneY + 2;
        const barHeight = LANE_HEIGHT - 4;
        const minWidth = 6;
        const barWidth = Math.max(endX - startX, minWidth);
        
        // Draw alert bar with rounded corners and severity color
        const radius = 2;
        ctx.beginPath();
        ctx.roundRect(startX, y, barWidth, barHeight, radius);
        ctx.fillStyle = getSeverityColorWithAlpha(alert.severity, 0.7);
        ctx.fill();
        
        // Draw border with severity color
        ctx.strokeStyle = getSeverityColor(alert.severity);
        ctx.lineWidth = 1;
        ctx.stroke();
      });
    });
    
    // Draw NOW line
    if (currentTime >= bounds.start && currentTime <= bounds.end) {
      ctx.strokeStyle = isDark ? 'rgba(255, 77, 79, 0.8)' : 'rgba(255, 77, 79, 0.9)';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(nowX, 0);
      ctx.lineTo(nowX, height - DATE_LABEL_HEIGHT);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // NOW label
      ctx.fillStyle = isDark ? 'rgba(255, 77, 79, 0.9)' : 'rgba(255, 77, 79, 1)';
      ctx.font = 'bold 8px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('NOW', nowX, 8);
    }
    
  }, [alerts, alertsByType, sessions, currentTime, theme, getEventBounds, dateToX, eventStartDate, canvasRenderKey]);
  
  // Check if mouse position is over a session
  const getSessionAtPosition = useCallback((x: number, y: number, bounds: { start: Date; end: Date }, width: number, height: number): Session | null => {
    const timelineWidth = width - LABEL_WIDTH;
    const timelineLeft = LABEL_WIDTH;
    
    if (x < timelineLeft || y > height - DATE_LABEL_HEIGHT) return null;
    
    for (const session of sessions) {
      const startX = timelineLeft + dateToX(new Date(session.start_time), bounds, timelineWidth);
      const endX = timelineLeft + dateToX(new Date(session.end_time), bounds, timelineWidth);
      
      if (x >= startX && x <= endX) {
        return session;
      }
    }
    
    return null;
  }, [sessions, dateToX]);
  
  // Handle mouse move for hover detection
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const bounds = getEventBounds();
    const timelineWidth = rect.width - LABEL_WIDTH;
    const timelineLeft = LABEL_WIDTH;
    
    // Check if hovering over any alert
    let foundAlert: EventAlert | null = null;
    
    // Determine which lane the mouse is in
    const laneIndex = Math.floor((y - SESSION_LABEL_HEIGHT) / (LANE_HEIGHT + LANE_PADDING));
    
    if (laneIndex >= 0 && laneIndex < ALERT_TYPE_LANES.length && x >= timelineLeft) {
      const lane = ALERT_TYPE_LANES[laneIndex];
      const laneAlerts = alertsByType.get(lane.type) || [];
      
      // Find alert at this x position
      for (const alert of laneAlerts) {
        const alertStart = new Date(alert.start_time);
        const alertEnd = new Date(alert.end_time);
        const startX = timelineLeft + dateToX(alertStart, bounds, timelineWidth);
        const endX = timelineLeft + dateToX(alertEnd, bounds, timelineWidth);
        const minWidth = 6;
        const barWidth = Math.max(endX - startX, minWidth);
        
        if (x >= startX && x <= startX + barWidth) {
          foundAlert = alert;
          break;
        }
      }
    }
    
    if (foundAlert) {
      setHoveredAlert(foundAlert);
      setHoveredSession(null);
      setTooltipPosition({ x: e.clientX, y: e.clientY - 120 });
      canvas.style.cursor = 'pointer';
    } else {
      setHoveredAlert(null);
      canvas.style.cursor = 'default';
      
      const session = getSessionAtPosition(x, y, bounds, rect.width, rect.height);
      
      if (session) {
        setHoveredSession(session);
        setTooltipPosition({ x: e.clientX, y: e.clientY - 80 });
      } else {
        setHoveredSession(null);
        setTooltipPosition(null);
      }
    }
  }, [alertsByType, getEventBounds, dateToX, getSessionAtPosition]);
  
  const handleMouseLeave = useCallback(() => {
    setHoveredAlert(null);
    setHoveredSession(null);
    setTooltipPosition(null);
    if (canvasRef.current) {
      canvasRef.current.style.cursor = 'default';
    }
  }, []);
  
  // Format time for tooltip display
  const formatTooltipTime = useCallback((timestamp: string) => {
    const tz = circuitTimeZone || 'UTC';
    return dayjs(timestamp).tz(tz).format('MMM D, HH:mm');
  }, [circuitTimeZone]);
  
  if (loading && alerts.length === 0) {
    return (
      <Card styles={{ body: { padding: '8px 12px' } }}>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spin size="small" />
        </div>
      </Card>
    );
  }
  
  if (!eventStartDate || !eventEndDate) {
    return (
      <Card styles={{ body: { padding: '8px 12px' } }}>
        <AntAlert title="No event selected" description="Please select an event to view alerts." type="info" showIcon />
      </Card>
    );
  }
  
  return (
    <Card 
      styles={{ body: { padding: '8px 12px' } }}
      className="alerts-timeline-card"
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span>Alerts Timeline</span>
            <span style={{ 
              fontSize: '12px', 
              fontWeight: 'normal',
              color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.65)',
              backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
              padding: '2px 10px',
              borderRadius: '4px',
            }}>
              {currentTimeDisplay}
              {circuitTimeZone && (
                <span style={{ 
                  color: theme === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
                  marginLeft: 6,
                }}>
                  ({circuitTimeZone})
                </span>
              )}
            </span>
            {eventDateStatus === 'upcoming' && hasAdminRole && (
              <Tag color="default">Event not started - Read only</Tag>
            )}
            {eventDateStatus === 'ended' && hasAdminRole && (
              <Tag color="default">Event ended - Read only</Tag>
            )}
          </div>
        </div>
      }
      extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Severity Legend */}
          <div style={{ display: 'flex', gap: '12px', fontSize: '11px', alignItems: 'center' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span className="legend-bar" style={{ backgroundColor: SEVERITY_CONFIG.info.color }} />
              Info
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span className="legend-bar" style={{ backgroundColor: SEVERITY_CONFIG.warning.color }} />
              Warning
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span className="legend-bar" style={{ backgroundColor: SEVERITY_CONFIG.critical.color }} />
              Critical
            </span>
          </div>
          {/* Action Buttons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {onRefresh && (
              <Tooltip title="Refresh">
                <Button
                  icon={<ReloadOutlined spin={refreshing} />}
                  onClick={onRefresh}
                  size="small"
                  type="text"
                />
              </Tooltip>
            )}
            {canManageAlerts && onCreateAlert && (
              <Tooltip title="Create Alert" placement="bottomRight">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={onCreateAlert}
                  size="small"
                />
              </Tooltip>
            )}
          </div>
        </div>
      }
    >
      {error && (
        <AntAlert
          title="Error"
          description={error}
          type="error"
          closable={{ onClose: onClearError }}
          style={{ marginBottom: 8 }}
        />
      )}
      
      {/* Canvas */}
      <div 
        ref={containerRef}
        className="alerts-timeline-container"
        style={{ height: canvasHeight }}
      >
        <canvas
          ref={canvasRef}
          className="alerts-timeline-canvas"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />
        
        {/* Alert Tooltip */}
        {hoveredAlert && tooltipPosition && (
          <div
            className={`alerts-timeline-tooltip ${theme === 'dark' ? 'dark' : 'light'}`}
            style={{
              left: tooltipPosition.x,
              top: tooltipPosition.y,
            }}
          >
            <div className="tooltip-header">
              <span 
                className="tooltip-severity"
                style={{ 
                  backgroundColor: getSeverityColorWithAlpha(hoveredAlert.severity, 0.2),
                  color: SEVERITY_CONFIG[hoveredAlert.severity].color,
                  borderColor: SEVERITY_CONFIG[hoveredAlert.severity].color,
                }}
              >
                {hoveredAlert.severity.toUpperCase()}
              </span>
              <span className="tooltip-type">
                {ALERT_TYPE_CONFIG[hoveredAlert.alert_type].icon} {ALERT_TYPE_CONFIG[hoveredAlert.alert_type].label}
              </span>
            </div>
            <div className="tooltip-title">{hoveredAlert.title}</div>
            <div className="tooltip-message">{hoveredAlert.message}</div>
            <div className="tooltip-time">
              <span>Start: {formatTooltipTime(hoveredAlert.start_time)}</span>
              <span>End: {formatTooltipTime(hoveredAlert.end_time)}</span>
            </div>
            {hoveredAlert.sessions && hoveredAlert.sessions.length > 0 && (
              <div className="tooltip-sessions">
                Sessions: {hoveredAlert.sessions.join(', ')}
              </div>
            )}
          </div>
        )}
        
        {/* Session Tooltip */}
        {hoveredSession && tooltipPosition && !hoveredAlert && (() => {
          // Format dates in the event's timezone
          const tz = circuitTimeZone || 'UTC';
          const formatCalendarDate = (dateStr: string, includeDate: boolean = true) => {
            const dateInEventTZ = dayjs.utc(dateStr).tz(tz);
            if (includeDate) {
              return dateInEventTZ.format('MMM D, HH:mm');
            } else {
              return dateInEventTZ.format('HH:mm');
            }
          };
          
          return (
            <div
              style={{
                position: 'fixed',
                left: tooltipPosition.x,
                top: tooltipPosition.y,
                transform: 'translateX(-50%)',
                backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                color: theme === 'dark' ? '#fff' : '#000',
                padding: '8px 12px',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'}`,
                pointerEvents: 'none',
                zIndex: 1000,
                fontSize: '12px',
                whiteSpace: 'nowrap',
                maxWidth: '300px',
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{hoveredSession.session_name}</div>
              <div style={{ opacity: 0.8, marginBottom: hoveredSession.description ? '4px' : '0' }}>
                {formatCalendarDate(hoveredSession.start_time, true)}
                {' - '}
                {formatCalendarDate(hoveredSession.end_time, false)}
              </div>
              {hoveredSession.description && (
                <div style={{ 
                  opacity: 0.7, 
                  fontSize: '11px', 
                  whiteSpace: 'normal',
                  lineHeight: '1.4'
                }}>
                  {hoveredSession.description}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </Card>
  );
};
